import React, { useEffect, useState, useContext, useCallback, Suspense, lazy } from 'react';
import { Card, Tabs, List, Button, Modal, Input, Space, Tag, message, Spin, Select, Popconfirm, Alert, Divider, Switch } from 'antd';
import { AuthContext } from '../../context/AuthContext';
import api from '../../api/http';
const DormHistoryTable = lazy(()=> import('./DormHistoryTable'));

// 宿舍管理页面
// 功能: 查看宿舍 -> 房间 -> 床位入住；可编辑床位姓名并保存
// 权限: dorm_view 只读；dorm_manage 可编辑

const DormManagementPage = ({ permissions = [] }) => {
  // 兼容：若外部未传递 permissions（当前 App.js 没有传），则从 AuthContext 获取
  const auth = useContext(AuthContext);
  const effectivePerms = permissions.length ? permissions : (auth?.user?.permissions || []);
  const canManage = effectivePerms.includes('dorm_manage');
  const [loading, setLoading] = useState(false);
  const [dorms, setDorms] = useState([]);
  const [activeDormId, setActiveDormId] = useState(null);
  const [editRoom, setEditRoom] = useState(null); // { id, occupants:[] }
  const [saving, setSaving] = useState(false);
  // 交互状态（入住/调床/历史）
  const [checkinModal, setCheckinModal] = useState(null); // {roomId, bed_index, occupant_name:''}
  const [moveModal, setMoveModal] = useState(null); // {from_room_id, from_bed_index, to_room_id:null, to_bed_index:null}
  const [historyModal, setHistoryModal] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  // 过滤相关状态
  const [filterName, setFilterName] = useState('');
  const [filterRoom, setFilterRoom] = useState('');
  const [showOnlyEmptyBeds, setShowOnlyEmptyBeds] = useState(false);

  const filterDormRooms = (dorm) => {
    let rooms = dorm.rooms;
    if (filterRoom.trim()) {
      const kw = filterRoom.trim();
      rooms = rooms.filter(r => String(r.room_number).includes(kw));
    }
    if (filterName.trim()) {
      const kw = filterName.trim();
      rooms = rooms.filter(r => r.occupants.some(o => !o.is_empty && (o.occupant_name || '').includes(kw)));
    }
    if (showOnlyEmptyBeds) {
      rooms = rooms.filter(r => r.occupants.some(o => o.is_empty));
    }
    return rooms;
  };
  const [createRoomModal, setCreateRoomModal] = useState(false);
  const [newRoomNumber, setNewRoomNumber] = useState('');
  const [newRoomBeds, setNewRoomBeds] = useState(4);
  const [creatingRoom, setCreatingRoom] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  // 宿舍元数据编辑/新增
  const [dormMetaModal, setDormMetaModal] = useState(false);
  const [editingDormMeta, setEditingDormMeta] = useState(null); // {id,name,address,landlord_phone,remark}
  const [dormMetaMode, setDormMetaMode] = useState('edit'); // edit | create
  const [dormMetaOccupantText, setDormMetaOccupantText] = useState(''); // 创建时的批量房间+入住文本

  const computeStats = (data) => {
    return data.map(d => {
      let beds = 0; let empty = 0; let occupied = 0;
      d.rooms.forEach(r => {
        beds += r.bed_count;
        r.occupants.forEach(o => { if (o.is_empty) empty++; else occupied++; });
      });
      const rate = beds ? (occupied / beds * 100) : 0;
      return { dormId: d.id, beds, empty, occupied, rate: parseFloat(rate.toFixed(2)) };
    });
  };

  const fetchDorms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/dorms');
      const dormData = res.data.dorms || [];
      setDorms(dormData);
      setStats(computeStats(dormData));
      if (!activeDormId && res.data.dorms && res.data.dorms.length) {
        setActiveDormId(res.data.dorms[0].id);
      }
    } catch(e){
      message.error('加载宿舍失败: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }, [activeDormId]);

  useEffect(()=>{ fetchDorms(); }, [fetchDorms]);

  const [stats, setStats] = useState([]);
  const currentDorm = dorms.find(d=>d.id===activeDormId);
  const openDormEdit = () => {
    if (!currentDorm) return;
    setDormMetaMode('edit');
    setEditingDormMeta({ id: currentDorm.id, name: currentDorm.name, address: currentDorm.address || '', landlord_phone: currentDorm.landlord_phone || '', remark: currentDorm.remark || '' });
    setDormMetaOccupantText('');
    setDormMetaModal(true);
  };
  const openDormCreate = () => {
    setDormMetaMode('create');
    setEditingDormMeta({ id:null, name:'', address:'', landlord_phone:'', remark:'' });
    setDormMetaOccupantText('');
    setDormMetaModal(true);
  };
  const parseOccupantText = (text) => {
    // 行格式: 房号[空格或制表]姓名1 姓名2 ...  '/' 表示空床; 忽略空行
    const lines = text.split(/\r?\n/).map(l=>l.trim()).filter(l=>l);
    const rooms = [];
    for (const line of lines) {
      const parts = line.split(/\s+/).filter(p=>p);
      if (parts.length < 2) continue; // 至少房号+1姓名
      const room_number = parts[0];
      const occs = parts.slice(1);
      rooms.push({ room_number, bed_count: occs.length, occupants: occs });
    }
    return rooms;
  };
  const saveDormMeta = async () => {
    if (!editingDormMeta) return;
    try {
      if (dormMetaMode === 'edit') {
        await api.put(`/dorms/${editingDormMeta.id}`, {
          name: editingDormMeta.name.trim(),
          address: editingDormMeta.address.trim() || null,
          landlord_phone: editingDormMeta.landlord_phone.trim() || null,
          remark: editingDormMeta.remark.trim() || null
        });
        message.success('宿舍信息已更新');
      } else {
        const payloadRooms = dormMetaOccupantText.trim() ? parseOccupantText(dormMetaOccupantText) : [];
        if (payloadRooms.length) {
          await api.post('/dorms/full', {
            name: editingDormMeta.name.trim(),
            address: editingDormMeta.address.trim() || null,
            landlord_phone: editingDormMeta.landlord_phone.trim() || null,
            remark: editingDormMeta.remark.trim() || null,
            rooms: payloadRooms
          });
        } else {
          await api.post('/dorms', {
            name: editingDormMeta.name.trim(),
            address: editingDormMeta.address.trim() || null,
            landlord_phone: editingDormMeta.landlord_phone.trim() || null,
            remark: editingDormMeta.remark.trim() || null
          });
        }
        message.success('宿舍已创建');
      }
      setDormMetaModal(false);
      setEditingDormMeta(null);
      await fetchDorms();
    } catch(e){
      message.error('保存失败: ' + (e.response?.data?.error || e.message));
    }
  };
  const deleteDorm = async () => {
    if (!editingDormMeta?.id) return;
    Modal.confirm({
      title:'确认删除宿舍?',
      content:'此操作将删除宿舍及其所有房间与床位数据，且不可恢复。',
      okText:'删除', okButtonProps:{ danger:true },
      onOk: async () => {
        try {
          await api.delete(`/dorms/${editingDormMeta.id}`);
          message.success('宿舍已删除');
          setDormMetaModal(false); setEditingDormMeta(null);
          await fetchDorms();
        } catch(e){ message.error('删除失败: ' + (e.response?.data?.error || e.message)); }
      }
    });
  };

  const openEdit = (room) => {
    if (!canManage) return;
    setEditRoom({ 
      ...room, 
      originalBedCount: room.bed_count,
      bed_count: room.bed_count,
      occupantsDraft: room.occupants.map(o=>({ bed_index: o.bed_index, occupant_name: o.is_empty? '' : (o.occupant_name || '') })) 
    });
  };

  const updateDraftName = (bedIndex, value) => {
    setEditRoom(prev => ({
      ...prev,
      occupantsDraft: prev.occupantsDraft.map(o => o.bed_index === bedIndex ? { ...o, occupant_name: value } : o)
    }));
  };

  const adjustDraftBeds = (nextCount) => {
    setEditRoom(prev => {
      if (!prev) return prev;
      const currentDraft = [...prev.occupantsDraft];
      const currentCount = currentDraft.length;
      if (nextCount > currentCount) {
        for (let i=currentCount+1; i<=nextCount; i++) {
          currentDraft.push({ bed_index:i, occupant_name:'' });
        }
      } else if (nextCount < currentCount) {
        // 只允许删除末尾且为空的床位
        for (let i=currentCount; i>nextCount; i--) {
          const last = currentDraft[i-1];
          if (last.occupant_name && last.occupant_name.trim()) {
            message.error(`床位 ${last.bed_index} 有人，不能减少。请先迁出或调床。`);
            return prev; // 不修改
          }
          currentDraft.pop();
        }
      }
      return { ...prev, bed_count: nextCount, occupantsDraft: currentDraft };
    });
  };

  const saveRoom = async () => {
    if (!editRoom) return;
    setSaving(true);
    try {
      // 若床位数变化，先调用 bed-count 接口
      if (editRoom.bed_count !== editRoom.originalBedCount) {
        await api.patch(`/dorms/rooms/${editRoom.id}/bed-count`, { bed_count: editRoom.bed_count });
      }
      const payload = editRoom.occupantsDraft.map(o => ({ bed_index: o.bed_index, occupant_name: (o.occupant_name || '').trim() || '/' }));
      await api.put(`/dorms/rooms/${editRoom.id}/occupants`, { occupants: payload });
      message.success('保存成功');
      setEditRoom(null);
      await fetchDorms();
    } catch(e){
      message.error('保存失败: ' + (e.response?.data?.error || e.message));
    } finally { setSaving(false); }
  };

  const exportCSV = () => {
    try {
      const lines = ['宿舍,房间,床位,姓名'];
      dorms.forEach(d => {
        d.rooms.forEach(r => {
          r.occupants.forEach(o => {
            lines.push(`${d.name},${r.room_number},${o.bed_index},${o.is_empty?'':(o.occupant_name||'')}`);
          });
        });
      });
      const blob = new Blob([lines.join('\n')], { type:'text/csv;charset=utf-8;' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `宿舍入住_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch(e){ message.error('导出失败: ' + e.message); }
  };

  // 入住/迁出/调床/历史相关函数
  const openCheckin = (roomId, bed_index) => setCheckinModal({ roomId, bed_index, occupant_name:'' });
  const doCheckin = async () => {
    if (!checkinModal) return;
    const name = (checkinModal.occupant_name || '').trim();
    if (!name) { message.warning('请输入姓名'); return; }
    try {
      await api.post(`/dorms/rooms/${checkinModal.roomId}/occupants/checkin`, { bed_index: checkinModal.bed_index, occupant_name: name });
      message.success('入住成功');
      setCheckinModal(null);
      await fetchDorms();
    } catch(e){ message.error('入住失败: ' + (e.response?.data?.error || e.message)); }
  };
  const doCheckout = async (roomId, bed_index) => {
    try {
      await api.delete(`/dorms/rooms/${roomId}/occupants/${bed_index}`);
      message.success('迁出成功');
      await fetchDorms();
    } catch(e){ message.error('迁出失败: ' + (e.response?.data?.error || e.message)); }
  };
  const openMove = (from_room_id, from_bed_index) => setMoveModal({ from_room_id, from_bed_index, to_room_id:null, to_bed_index:null });
  const doMove = async () => {
    if (!moveModal?.to_room_id || !moveModal?.to_bed_index) { message.warning('请选择目标房间与床位'); return; }
    try {
      await api.patch('/dorms/move', moveModal);
      message.success('调床成功');
      setMoveModal(null);
      await fetchDorms();
    } catch(e){ message.error('调床失败: ' + (e.response?.data?.error || e.message)); }
  };
  const openHistory = async () => {
    setHistoryModal(true);
    setHistoryLoading(true);
    try {
      const res = await api.get('/dorms/history?limit=200');
      setHistoryItems(res.data.items || []);
    } catch(e){ message.error('加载历史失败: ' + (e.response?.data?.error || e.message)); }
    finally { setHistoryLoading(false); }
  };
  const actionLabel = (t) => t==='checkin'?'入住':(t==='adjust'?'调床':'迁出');

  const renderRoomCard = (room) => {
    const emptyBeds = room.occupants.filter(o=>o.is_empty).length;
    const cardStyle = emptyBeds > 0 ? { border:'1px solid #faad14' } : {};
    return (
      <Card size="small" title={`${room.room_number} (${room.bed_count}床)`} style={{ marginBottom:8, ...cardStyle }} extra={canManage && <Button size="small" onClick={()=>openEdit(room)}>编辑</Button>}>
        <List
          size="small"
          dataSource={room.occupants}
          renderItem={occ => (
            <List.Item
              style={{
                display:'flex',
                alignItems:'center',
                justifyContent:'space-between',
                gap:6,
                flexWrap:'wrap'
              }}
            >
              <div style={{ display:'flex', alignItems:'center', flexWrap:'wrap', gap:4, minWidth:0 }}>
                <Tag color={occ.is_empty? 'default':'blue'} style={{ marginRight:4 }}>床{occ.bed_index}</Tag>
                {occ.is_empty ? (
                  <span style={{ color:'#999' }}>空床</span>
                ) : (
                  <span style={{ maxWidth:90, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }} title={occ.occupant_name}>{occ.occupant_name}</span>
                )}
                {occ.department && (
                  <Tag color="green" style={{ maxWidth:100, whiteSpace:'nowrap', textOverflow:'ellipsis', overflow:'hidden' }} title={occ.department}>{occ.department}</Tag>
                )}
              </div>
              {canManage && (
                <div style={{ display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
                  {occ.is_empty ? (
                    <Button size="small" type="link" onClick={()=>openCheckin(room.id, occ.bed_index)} style={{ padding:0 }}>入住</Button>
                  ) : (
                    <>
                      <Popconfirm title="确认迁出?" onConfirm={()=>doCheckout(room.id, occ.bed_index)}>
                        <Button size="small" type="link" danger style={{ padding:0 }}>迁出</Button>
                      </Popconfirm>
                      <Button size="small" type="link" onClick={()=>openMove(room.id, occ.bed_index)} style={{ padding:0 }}>调床</Button>
                    </>
                  )}
                </div>
              )}
            </List.Item>
          )}
        />
      </Card>
    );
  };

  if (loading && !dorms.length) return <Spin />;

  return (
  <Card title="宿舍管理" extra={<Space wrap>
    <Button onClick={fetchDorms} size="small" loading={loading}>刷新</Button>
    {canManage && <Button size="small" onClick={openDormCreate}>新增宿舍</Button>}
    {canManage && <Button size="small" onClick={openDormEdit} disabled={!currentDorm}>编辑宿舍</Button>}
    <Button size="small" onClick={()=> setCreateRoomModal(true)} disabled={!canManage || !activeDormId}>新增房间</Button>
    <Button size="small" onClick={exportCSV} disabled={!dorms.length}>导出CSV</Button>
    <Button size="small" onClick={openHistory}>历史记录</Button>
  </Space>}>
      {!canManage && <Alert type="info" showIcon style={{ marginBottom:12 }} message="当前为只读模式。需要权限 dorm_manage 才能执行 入住 / 调床 / 迁出 操作。请为角色添加该权限或使用管理员账号。" />}
      <div style={{ display:'flex', gap:8, overflowX:'auto', paddingBottom:8, marginBottom:12 }}>
        {stats.map(s => (
          <Card key={s.dormId} size="small" bodyStyle={{ padding:8, minWidth:160 }} style={{ flex:'0 0 auto', background:'#fafafa', border:'1px solid #eee' }}>
            <div style={{ fontSize:12, fontWeight:600 }}>{dorms.find(d=>d.id===s.dormId)?.name}</div>
            <div style={{ fontSize:20, fontWeight:600 }}>{s.rate}%</div>
            <div style={{ fontSize:11 }}>床:{s.beds} 入:{s.occupied} 空:{s.empty}</div>
          </Card>
        ))}
      </div>
      <Tabs
        activeKey={activeDormId ? String(activeDormId) : undefined}
        onChange={key=> setActiveDormId(Number(key))}
        items={dorms.map(d=>({ key:String(d.id), label:d.name, children:(
          <div>
            {d.address && <p style={{ margin:'4px 0 12px 0', color:'#666' }}>地址: {d.address}</p>}
            <Space wrap style={{ marginBottom:12 }}>
              <Input size="small" placeholder="按姓名搜索" allowClear value={filterName} onChange={e=> setFilterName(e.target.value)} style={{ width:140 }} />
              <Input size="small" placeholder="按房号搜索" allowClear value={filterRoom} onChange={e=> setFilterRoom(e.target.value)} style={{ width:120 }} />
              <Space size={4}>
                <Switch size="small" checked={showOnlyEmptyBeds} onChange={v=> setShowOnlyEmptyBeds(v)} />
                <span style={{ fontSize:12 }}>只看有空床房间</span>
              </Space>
              <Button size="small" onClick={()=> { setFilterName(''); setFilterRoom(''); setShowOnlyEmptyBeds(false); }}>重置筛选</Button>
            </Space>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(250px,1fr))', gap:12 }}>
              {filterDormRooms(d).map(r=> renderRoomCard(r))}
            </div>
          </div>
        )}))}
      />
      {/* 移除旧的简单编辑 Modal，保留带床位增减的新版 Modal */}
      <Modal
        open={!!checkinModal}
        title={checkinModal ? `入住 - 房间 ${currentDorm?.rooms.find(r=>r.id===checkinModal.roomId)?.room_number} 床位 ${checkinModal.bed_index}` : ''}
        onCancel={()=> setCheckinModal(null)}
        onOk={doCheckin}
        okText="确认入住"
      >
        {checkinModal && (
          <Input
            autoFocus
            placeholder="输入入住人姓名"
            value={checkinModal.occupant_name}
            onChange={e=> setCheckinModal({...checkinModal, occupant_name:e.target.value})}
          />
        )}
      </Modal>
      <Modal
        open={dormMetaModal}
        title={dormMetaMode==='edit'? '编辑宿舍' : '新增宿舍'}
        onCancel={()=> { setDormMetaModal(false); setEditingDormMeta(null); }}
        onOk={saveDormMeta}
        okText="保存"
        width={600}
      >
        {editingDormMeta && (
          <Space direction="vertical" style={{ width:'100%' }} size={12}>
            <Input
              placeholder="宿舍名称"
              value={editingDormMeta.name}
              onChange={e=> setEditingDormMeta({ ...editingDormMeta, name:e.target.value })}
            />
            <Input
              placeholder="地址"
              value={editingDormMeta.address}
              onChange={e=> setEditingDormMeta({ ...editingDormMeta, address:e.target.value })}
            />
            <Input
              placeholder="房东电话"
              value={editingDormMeta.landlord_phone}
              onChange={e=> setEditingDormMeta({ ...editingDormMeta, landlord_phone:e.target.value })}
            />
            <Input.TextArea
              placeholder="备注"
              value={editingDormMeta.remark}
              rows={3}
              onChange={e=> setEditingDormMeta({ ...editingDormMeta, remark:e.target.value })}
            />
            {dormMetaMode==='create' && (
              <Input.TextArea
                placeholder={"可选：批量房间入住数据（每行：房号 空格 姓名1 姓名2 ...，使用 / 表示空床）\n例如：\n306 黄汝辉 官国勤\n401 江维加 张超先 温炘华"}
                value={dormMetaOccupantText}
                onChange={e=> setDormMetaOccupantText(e.target.value)}
                rows={8}
              />
            )}
            {dormMetaMode==='edit' && canManage && (
              <Button danger onClick={deleteDorm}>删除宿舍</Button>
            )}
          </Space>
        )}
      </Modal>
      <Modal
        open={!!moveModal}
        title={moveModal ? `调床 - 来源房间 ${currentDorm?.rooms.find(r=>r.id===moveModal.from_room_id)?.room_number} 床位 ${moveModal.from_bed_index}` : ''}
        onCancel={()=> setMoveModal(null)}
        onOk={doMove}
        okText="执行调床"
      >
        {moveModal && (
          <Space direction="vertical" style={{ width:'100%' }}>
            <Select
              placeholder="选择目标房间"
              value={moveModal.to_room_id}
              onChange={v=> setMoveModal({...moveModal, to_room_id:v, to_bed_index:null})}
              style={{ width:'100%' }}
              options={currentDorm?.rooms.map(r=>({ value:r.id, label:r.room_number }))}
            />
            {moveModal.to_room_id && (
              <Select
                placeholder="选择目标床位"
                value={moveModal.to_bed_index}
                onChange={v=> setMoveModal({...moveModal, to_bed_index:v})}
                style={{ width:'100%' }}
                options={currentDorm?.rooms.find(r=>r.id===moveModal.to_room_id)?.occupants.filter(o=>o.bed_index!==moveModal.from_bed_index).map(o=>({ value:o.bed_index, label: o.is_empty?`床${o.bed_index} (空)`:`床${o.bed_index} (${o.occupant_name||'占用'})` }))}
              />
            )}
          </Space>
        )}
      </Modal>
      <Modal
        open={historyModal}
        title="入住/调床/迁出历史"
        width={800}
        footer={<Button onClick={()=> setHistoryModal(false)}>关闭</Button>}
        onCancel={()=> setHistoryModal(false)}
      >
        {historyLoading ? <Spin /> : (
          <Suspense fallback={<Spin />}> 
            <DormHistoryTable items={historyItems} actionLabel={actionLabel} />
          </Suspense>
        )}
      </Modal>
      <Modal
        open={createRoomModal}
        title="新增房间"
        onCancel={()=> setCreateRoomModal(false)}
        onOk={async ()=> {
          if (!newRoomNumber.trim()) { message.warning('请输入房号'); return; }
          setCreatingRoom(true);
          try {
            await api.post(`/dorms/${activeDormId}/rooms`, { room_number:newRoomNumber.trim(), bed_count:newRoomBeds });
            message.success('房间创建成功');
            setCreateRoomModal(false);
            setNewRoomNumber('');
            setNewRoomBeds(4);
            await fetchDorms();
          } catch(e){ message.error('创建失败: ' + (e.response?.data?.error || e.message)); }
          finally { setCreatingRoom(false); }
        }}
        confirmLoading={creatingRoom}
      >
        <Space direction="vertical" style={{ width:'100%' }}>
          <Input placeholder="房号" value={newRoomNumber} onChange={e=> setNewRoomNumber(e.target.value)} />
          <Space>
            <span>床位数量:</span>
            <Button size="small" disabled={newRoomBeds<=1} onClick={()=> setNewRoomBeds(c=>c-1)}>-</Button>
            <span style={{ minWidth:30, textAlign:'center' }}>{newRoomBeds}</span>
            <Button size="small" disabled={newRoomBeds>=50} onClick={()=> setNewRoomBeds(c=>c+1)}>+</Button>
          </Space>
          <Alert type="info" message="创建后可再编辑调整床位。减少床位需先迁出末尾床位人员。" />
        </Space>
      </Modal>
      {editRoom && (
        <div style={{ display:'none' }}></div>
      )}
      <Modal
        open={!!editRoom}
        title={editRoom ? `编辑房间 ${editRoom.room_number}` : ''}
        onCancel={()=> setEditRoom(null)}
        onOk={saveRoom}
        confirmLoading={saving}
        okText="保存"
        width={620}
      >
        {editRoom && (
          <>
            <Space style={{ marginBottom:12 }}>
              <span>床位数量:</span>
              <Button size="small" disabled={editRoom.bed_count<=1} onClick={()=> adjustDraftBeds(editRoom.bed_count-1)}>-</Button>
              <span style={{ minWidth:30, textAlign:'center' }}>{editRoom.bed_count}</span>
              <Button size="small" disabled={editRoom.bed_count>=50} onClick={()=> adjustDraftBeds(editRoom.bed_count+1)}>+</Button>
              {editRoom.bed_count !== editRoom.originalBedCount && <Tag color="orange">已修改</Tag>}
            </Space>
            <Divider style={{ margin:'8px 0' }} />
            <List
              size="small"
              dataSource={editRoom.occupantsDraft}
              renderItem={o => (
                <List.Item>
                  <Space>
                    <Tag color={o.occupant_name? 'blue':'default'}>床{o.bed_index}</Tag>
                    <Input
                      allowClear
                      placeholder="姓名或留空表示空床"
                      style={{ width:240 }}
                      value={o.occupant_name}
                      onChange={e=> updateDraftName(o.bed_index, e.target.value)}
                    />
                  </Space>
                </List.Item>
              )}
            />
          </>
        )}
        {!editRoom && <Spin />}
      </Modal>
    </Card>
  );
};

export default DormManagementPage;